import React, { useCallback, useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"

import BookingPolicyTileIcon from "@/components/booking_policy_tile_icon"
import i18n from "@/i18n"
import { getBookingPolicyTileTitle, type BookingPolicyTileViewModel } from "@/shared/booking_policy"

const STYLES = {
  wrapper: "mb-8 border-t border-[#EFEFEF] pt-6",
  title: "mb-4 mt-0 text-base font-medium text-[#222222]",
  tileGrid: "mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3",
  tile: "flex items-start gap-3",
  tileMuted: "opacity-45",
  tileIcon: "flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-[#f5f7f9] text-[#97a0ad]",
  tileTitle: "text-[12px] font-medium leading-[17px] text-black",
  tileSubtitle: "mt-0 text-[10px] leading-[14px] text-[#616161]",
  cardBand: "-mx-4 mt-4 bg-[#d9d9d9]/10 px-4 py-3",
  cardGrid: "grid grid-cols-2 gap-2",
  cardTile:
    "flex h-[46px] items-center gap-2 rounded-[6px] border-[0.5px] border-[#e7e7e7] bg-white px-[10px] py-[6px]",
  cardTileIcon: "flex h-[35px] w-[35px] shrink-0 items-center justify-center rounded-full bg-[#f5f7f9] text-[#97a0ad]",
  cardTileTitle: "text-[11px] leading-[14px] text-[#111827]",
  cardTileSubtitle: "mt-[3px] truncate text-[8px] leading-[10px] text-[#838fa0]",
  panel: "mt-5 overflow-hidden rounded-[10px] bg-[#fafafa] px-5 py-5 transition-[max-height] duration-300 ease-out",
  panelCollapsed: "max-h-[132px]",
  panelExpanded: "max-h-none",
  markdown:
    "prose prose-sm max-w-none text-[12px] leading-[18px] text-[#5f6774] prose-p:my-0 prose-ul:my-0 prose-li:my-0 prose-strong:text-[#444] prose-strong:font-semibold",
  toggle: "mt-4 border-0 bg-transparent p-0 text-[14px] font-medium leading-[20px] text-[#2da0fe] cursor-pointer",
} as const

type BookingPolicyProps = {
  tiles: BookingPolicyTileViewModel[]
  title?: string | null
  content?: string | null
  showDisabledTiles?: boolean
  testIdPrefix?: string
  hideTitle?: boolean
  embedded?: boolean
  cardTiles?: boolean
  wrapperClassName?: string
}

const BookingPolicy: React.FC<BookingPolicyProps> = ({
  tiles,
  title,
  content,
  showDisabledTiles = false,
  testIdPrefix = "booking-policy",
  hideTitle = false,
  embedded = false,
  cardTiles = false,
  wrapperClassName,
}) => {
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const hasContent = Boolean(content?.trim())

  const checkOverflow = useCallback(() => {
    if (contentRef.current) {
      setOverflows(contentRef.current.scrollHeight > 132)
    }
  }, [])

  useEffect(() => {
    checkOverflow()
  }, [content, checkOverflow])

  useEffect(() => {
    const el = contentRef.current
    if (!el || typeof ResizeObserver === "undefined") return

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(el)
    return () => observer.disconnect()
  }, [checkOverflow])

  if (!tiles.length && !hasContent) {
    return null
  }

  return (
    <section className={embedded ? "" : wrapperClassName ?? STYLES.wrapper} data-testid={`${testIdPrefix}-section`}>
      {!hideTitle && <h3 className={STYLES.title}>{title || i18n.t("app.booking_policy.title")}</h3>}

      {tiles.length > 0 &&
        (cardTiles ? (
          <div className={STYLES.cardBand} data-testid={`${testIdPrefix}-tiles`}>
            <div className={STYLES.cardGrid}>
              {tiles.map((tile) => (
                <div
                  key={tile.kind}
                  className={`${STYLES.cardTile} ${showDisabledTiles && !tile.enabled ? STYLES.tileMuted : ""}`.trim()}
                  data-testid={`${testIdPrefix}-tile-${tile.kind}`}
                >
                  <div className={STYLES.cardTileIcon}>
                    <BookingPolicyTileIcon kind={tile.kind} className="h-[18px] w-[18px]" />
                  </div>
                  <div className="min-w-0">
                    <div className={STYLES.cardTileTitle}>{getBookingPolicyTileTitle(tile.kind)}</div>
                    <div className={STYLES.cardTileSubtitle}>{tile.subtitle}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className={hideTitle ? "grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3" : STYLES.tileGrid}
            data-testid={`${testIdPrefix}-tiles`}
          >
            {tiles.map((tile) => {
              return (
                <div
                  key={tile.kind}
                  className={`${STYLES.tile} ${showDisabledTiles && !tile.enabled ? STYLES.tileMuted : ""}`.trim()}
                  data-testid={`${testIdPrefix}-tile-${tile.kind}`}
                >
                  <div className={STYLES.tileIcon}>
                    <BookingPolicyTileIcon kind={tile.kind} className="h-[18px] w-[18px]" />
                  </div>
                  <div className="min-w-0">
                    <div className={STYLES.tileTitle}>{getBookingPolicyTileTitle(tile.kind)}</div>
                    <div className={STYLES.tileSubtitle}>{tile.subtitle}</div>
                  </div>
                </div>
              )
            })}
          </div>
        ))}

      {!!hasContent && (
        <>
          <div
            ref={contentRef}
            className={`${STYLES.panel} ${expanded || !overflows ? STYLES.panelExpanded : STYLES.panelCollapsed}`}
            data-testid={`${testIdPrefix}-details-panel`}
          >
            <div className={STYLES.markdown}>
              <ReactMarkdown>{content || ""}</ReactMarkdown>
            </div>
          </div>

          {!!overflows && (
            <button
              type="button"
              className={STYLES.toggle}
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              data-testid={`${testIdPrefix}-toggle`}
            >
              {expanded ? `- ${i18n.t("app.booking_policy.show_less")}` : `+ ${i18n.t("app.booking_policy.show_more")}`}
            </button>
          )}
        </>
      )}
    </section>
  )
}

export default BookingPolicy
